import { formDataSchema } from "./schemas";
import { FormData } from "./types";
import { DEFAULT_CHARACTER } from "@/constants/character";
import { DEFAULT_AUDIO_VOICE } from "@/constants/audioVoice";

const STORAGE_KEY = "sessionFormData";
export const PREVIEW_STORAGE_KEY = "previewFormData";

// Migration: map old voice enum values to new character + audio voice IDs
const OLD_VOICE_TO_CHARACTER: Record<string, string> = {
  oliver: "oliver",
  amelia: "amelia",
  chloe: "chloe",
  aaliyah: "aaliyah",
  madison: "madison",
  grace: "grace",
  jasper: "logan",
  jackson: "jackson",
  yasmeen: "yasmeen",
  fahad: "fahad",
  nura: "nura",
};

const OLD_VOICE_TO_AUDIO_VOICE: Record<string, string> = {
  oliver: "oliver",
  amelia: "amelia",
  chloe: "chloe",
  aaliyah: "aaliyah",
  madison: "madison",
  grace: "grace",
  jasper: "jasper",
  jackson: "jackson",
  yasmeen: "chloe",
  fahad: "oliver",
  nura: "amelia",
  emma: "grace",
  nora: "aaliyah",
  isla: "amelia",
  priya: "chloe",
  aria: "madison",
  freya: "amelia",
  ethan: "jackson",
  henry: "oliver",
  rohan: "rohan",
  leo: "jackson",
  marcus: "jackson",
  theo: "jackson",
};

function migrateV1(parsed: unknown): FormData | null {
  try {
    const old = parsed as {
      language?: unknown;
      voice_id?: { voice?: string; speed?: string };
    };
    if (
      old?.voice_id &&
      !("character" in (old as Record<string, unknown>)) &&
      !("voice" in (old as Record<string, unknown>))
    ) {
      const oldVoice = old.voice_id.voice || "";
      return {
        language: old.language as FormData["language"],
        character: {
          characterId: OLD_VOICE_TO_CHARACTER[oldVoice] || DEFAULT_CHARACTER.id,
        },
        voice: {
          voiceId: OLD_VOICE_TO_AUDIO_VOICE[oldVoice] || DEFAULT_AUDIO_VOICE.id,
          speed: old.voice_id.speed || DEFAULT_AUDIO_VOICE.defaultSpeed,
        },
      };
    }
  } catch {
    /* fall through */
  }
  return null;
}

export const getStoredFormData = (
  fallback: FormData,
  key?: string,
): FormData => {
  const k = key ?? STORAGE_KEY;
  try {
    const stored = localStorage.getItem(k);
    if (stored) {
      const parsed = JSON.parse(stored);

      // Try parsing as current schema first
      const result = formDataSchema.safeParse(parsed);
      if (result.success) {
        return result.data as FormData;
      }

      // Attempt v1 migration (old voice_id format)
      const migrated = migrateV1(parsed);
      if (migrated) {
        const migratedResult = formDataSchema.safeParse(migrated);
        if (migratedResult.success) {
          localStorage.setItem(k, JSON.stringify(migratedResult.data));
          return migratedResult.data as FormData;
        }
      }

      console.warn("Invalid form data in localStorage, resetting to defaults");
      localStorage.setItem(k, JSON.stringify(fallback));
    }
  } catch (error) {
    console.error("Error loading form data from localStorage:", error);
    localStorage.removeItem(k);
  }
  return fallback;
};

export const saveFormData = (formData: FormData, key?: string): boolean => {
  const k = key ?? STORAGE_KEY;
  const result = formDataSchema.safeParse(formData);
  if (result.success) {
    try {
      localStorage.setItem(k, JSON.stringify(formData));
      return true;
    } catch (error) {
      console.error("Error saving form data to localStorage:", error);
      return false;
    }
  } else {
    console.error("Invalid form data detected:", result.error);
    return false;
  }
};

export const clearStoredFormData = (key?: string): void => {
  localStorage.removeItem(key ?? STORAGE_KEY);
};
